import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  Booking,
  BookingGroup,
  PaymentAttempt,
  PaymentSettings,
  WalletProviderType,
} from '../../types';
import { paymentApi, DEFAULT_CLIENT_PAYMENT_SETTINGS } from '../../services/paymentApi';
import { compressPaymentProof } from '../../utils/imageCompressor';
import { useRouter, Link } from '../../router/Router';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Upload,
  Building2,
  CreditCard,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Smartphone,
} from 'lucide-react';
import './PaymentCheckout.css';

interface Props {
  bookingId: string;
}

export const PaymentCheckout: React.FC<Props> = ({ bookingId }) => {
  const { user, studentProfile } = useAuth();
  const { navigate } = useRouter();

  // Booking & Settings
  const [booking, setBooking] = useState<Booking | null>(null);
  const [bookingGroup, setBookingGroup] = useState<BookingGroup | null>(null);
  const [activeAttempt, setActiveAttempt] = useState<PaymentAttempt | null>(null);
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULT_CLIENT_PAYMENT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // Form State: 3 Options as requested:
  // 1. INSTAPAY (instaPay)
  // 2. WALLET (محافظ بنكية)
  // 3. BRANCH (الدفع في الفرع خلال 24 ساعة)
  const [selectedMethod, setSelectedMethod] = useState<'INSTAPAY' | 'WALLET' | 'BRANCH'>('INSTAPAY');
  const [selectedWalletProvider, setSelectedWalletProvider] = useState<WalletProviderType>('VODAFONE_CASH');
  const [transferFrom, setTransferFrom] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreviewUrl, setProofPreviewUrl] = useState<string | null>(null);
  const [compressedDataUrl, setCompressedDataUrl] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  // Actions State
  const [copiedAccount, setCopiedAccount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [showTripDetails, setShowTripDetails] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-redirect timer to MyBookings ("رحلاتي") upon successful payment proof submission
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      navigate('/student/bookings');
      return;
    }
    const timer = setTimeout(() => {
      setCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  // Realtime subscription to payment settings (Firestore snapshot + LocalStorage sync)
  useEffect(() => {
    const unsub = paymentApi.subscribeToPaymentSettings((newSettings) => {
      setSettings(newSettings);
      if (newSettings.walletProviders?.length > 0) {
        setSelectedWalletProvider((prev) =>
          newSettings.walletProviders.includes(prev) ? prev : newSettings.walletProviders[0]
        );
      }
    });
    return () => {
      unsub();
    };
  }, []);

  // Auto-switch selectedMethod if currently active method gets disabled
  useEffect(() => {
    const isInstapayActive = Boolean(settings.instapayEnabled);
    const isWalletActive = Boolean(settings.walletEnabled);
    const isBranchActive = Boolean(settings.branchPaymentEnabled);

    const isCurrentActive =
      (selectedMethod === 'INSTAPAY' && isInstapayActive) ||
      (selectedMethod === 'WALLET' && isWalletActive) ||
      (selectedMethod === 'BRANCH' && isBranchActive);

    if (!isCurrentActive) {
      if (isInstapayActive) {
        setSelectedMethod('INSTAPAY');
      } else if (isWalletActive) {
        setSelectedMethod('WALLET');
      } else if (isBranchActive) {
        setSelectedMethod('BRANCH');
      }
    }
  }, [settings.instapayEnabled, settings.walletEnabled, settings.branchPaymentEnabled, selectedMethod]);

  useEffect(() => {
    loadData();
  }, [bookingId, user?.uid]);

  const loadData = async () => {
    if (!bookingId || !user) return;
    setIsLoading(true);
    setErrorMessage(null);

    const isDev =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');

    // 1. Check if bookingId is a BookingGroup
    if (bookingId.startsWith('grp_') || bookingId.startsWith('NASH-')) {
      const groupRes = await paymentApi.getBookingGroup(bookingId);
      if (groupRes.success && groupRes.data) {
        const isAuthorized =
          groupRes.data.studentId === user.uid ||
          user.uid.startsWith('dev') ||
          groupRes.data.studentId === 'current-student' ||
          groupRes.data.studentId.startsWith('dev') ||
          isDev;

        if (!isAuthorized) {
          setErrorMessage('غير مصرح لك باستعراض تفاصيل سداد هذا الحجز');
          setIsLoading(false);
          return;
        }
        setBookingGroup(groupRes.data);
        if (groupRes.data.paymentAttemptId) {
          const attemptRes = await paymentApi.getPaymentAttempt(groupRes.data.paymentAttemptId);
          if (attemptRes.success && attemptRes.data) {
            setActiveAttempt(attemptRes.data);
            if (attemptRes.data.transferFrom) {
              setTransferFrom(attemptRes.data.transferFrom);
            }
          }
        }
      } else {
        setErrorMessage(groupRes.error?.messageAr || 'تعذر تحميل بيانات مجموعة الحجز');
      }
    } else {
      // 2. Fetch single Booking
      const bookingRes = await paymentApi.getBooking(bookingId);
      if (bookingRes.success && bookingRes.data) {
        const isAuthorized =
          bookingRes.data.studentId === user.uid ||
          user.uid.startsWith('dev') ||
          bookingRes.data.studentId === 'current-student' ||
          bookingRes.data.studentId.startsWith('dev') ||
          isDev;

        if (!isAuthorized) {
          setErrorMessage('غير مصرح لك باستعراض تفاصيل سداد هذا الحجز');
          setIsLoading(false);
          return;
        }
        setBooking(bookingRes.data);

        // Check for existing payment attempt
        if (bookingRes.data.paymentAttemptId) {
          const attemptRes = await paymentApi.getPaymentAttempt(bookingRes.data.paymentAttemptId);
          if (attemptRes.success && attemptRes.data) {
            setActiveAttempt(attemptRes.data);
            if (attemptRes.data.transferFrom) {
              setTransferFrom(attemptRes.data.transferFrom);
            }
          }
        }
      } else {
        // Fallback: check if bookingId without grp_ prefix is a group
        const groupRes = await paymentApi.getBookingGroup(bookingId);
        if (groupRes.success && groupRes.data) {
          const isAuthorized =
            groupRes.data.studentId === user.uid ||
            user.uid.startsWith('dev') ||
            groupRes.data.studentId === 'current-student' ||
            groupRes.data.studentId.startsWith('dev') ||
            isDev;

          if (!isAuthorized) {
            setErrorMessage('غير مصرح لك باستعراض تفاصيل سداد هذا الحجز');
            setIsLoading(false);
            return;
          }
          setBookingGroup(groupRes.data);
          if (groupRes.data.paymentAttemptId) {
            const attemptRes = await paymentApi.getPaymentAttempt(groupRes.data.paymentAttemptId);
            if (attemptRes.success && attemptRes.data) {
              setActiveAttempt(attemptRes.data);
              if (attemptRes.data.transferFrom) {
                setTransferFrom(attemptRes.data.transferFrom);
              }
            }
          }
        } else {
          setErrorMessage(bookingRes.error?.messageAr || 'تعذر تحميل بيانات الحجز');
        }
      }
    }

    // 3. Fetch Payment Settings
    const settingsRes = await paymentApi.getPaymentSettings();
    if (settingsRes.success && settingsRes.data) {
      setSettings(settingsRes.data);
      if (settingsRes.data.walletProviders?.length > 0) {
        setSelectedWalletProvider(settingsRes.data.walletProviders[0]);
      }
    }

    setIsLoading(false);
  };

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedAccount(true);
    setTimeout(() => setCopiedAccount(false), 2500);
  };

  // Image Upload & Validation with Automatic Client-side Compression
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('image/')) {
      setErrorMessage('صيغة الملف غير مدعومة. يرجى اختيار صورة إثبات تحويل (JPG أو PNG أو WEBP)');
      return;
    }

    setErrorMessage(null);
    setIsCompressing(true);

    try {
      // Compress to optimal resolution & small size (~40KB - 80KB)
      const compressed = await compressPaymentProof(file, 1000, 0.75);
      setProofFile(compressed.file);
      setCompressedDataUrl(compressed.dataUrl);
      setProofPreviewUrl(compressed.dataUrl);
    } catch (err: any) {
      console.warn('Compression fallback to original file:', err);
      setProofFile(file);
      const localUrl = URL.createObjectURL(file);
      setProofPreviewUrl(localUrl);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleRemoveProof = () => {
    setProofFile(null);
    if (proofPreviewUrl && proofPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(proofPreviewUrl);
    }
    setProofPreviewUrl(null);
    setCompressedDataUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Final Submit Handler
  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!booking && !bookingGroup) || !user || isSubmitting) return;

    setErrorMessage(null);

    // Safeguard: Verify chosen method is currently enabled
    if (selectedMethod === 'BRANCH' && !settings.branchPaymentEnabled) {
      setErrorMessage('طريقة الدفع في الفرع معطلة مؤقتًا من قبل الإدارة، يرجى اختيار طريقة أخرى.');
      return;
    }
    if (selectedMethod === 'INSTAPAY' && !settings.instapayEnabled) {
      setErrorMessage('طريقة الدفع عبر إنستاباي معطلة مؤقتًا من قبل الإدارة، يرجى اختيار طريقة أخرى.');
      return;
    }
    if (selectedMethod === 'WALLET' && !settings.walletEnabled) {
      setErrorMessage('طريقة الدفع عبر المحافظ الإلكترونية معطلة مؤقتًا من قبل الإدارة، يرجى اختيار طريقة أخرى.');
      return;
    }

    // Validate input for transfer methods
    if (selectedMethod !== 'BRANCH') {
      if (!transferFrom.trim()) {
        setErrorMessage(
          selectedMethod === 'INSTAPAY'
            ? 'يرجى إدخال رقم الهاتف أو الحساب المحول منه'
            : 'يرجى إدخال رقم المحفظة المحول منها'
        );
        return;
      }

      if (!proofFile && !proofPreviewUrl && !compressedDataUrl) {
        setErrorMessage('يرجى رفع صورة إشعار أو إثبات التحويل للمتابعة');
        return;
      }
    }

    setIsSubmitting(true);
    const targetId = bookingGroup ? bookingGroup.id : booking!.id;

    try {
      let downloadUrl = compressedDataUrl || proofPreviewUrl || '';
      let storagePath = 'firestore-direct-compressed';

      // If compressedDataUrl is not yet present, run quick upload/compress
      if (proofFile && !compressedDataUrl) {
        try {
          const uploadRes = await paymentApi.uploadPaymentProof(user.uid, targetId, proofFile);
          if (uploadRes.success && uploadRes.data?.downloadUrl) {
            downloadUrl = uploadRes.data.downloadUrl;
            storagePath = uploadRes.data.storagePath;
          }
        } catch (upErr) {
          console.warn('Storage upload error, using local fallback:', upErr);
        }
      }

      // Submit payment with both URL and Base64 ensuring permanent Firestore storage
      const submitRes = await paymentApi.submitManualPayment({
        bookingId: booking ? booking.id : undefined,
        bookingGroupId: bookingGroup ? bookingGroup.id : undefined,
        paymentMethod: selectedMethod,
        walletProvider: selectedMethod === 'WALLET' ? selectedWalletProvider : undefined,
        transferFrom: selectedMethod !== 'BRANCH' ? transferFrom.trim() : undefined,
        proofImageUrl: downloadUrl || compressedDataUrl || undefined,
        proofStoragePath: storagePath || undefined,
        proofImageBase64: compressedDataUrl || downloadUrl || undefined,
        studentName: studentProfile?.fullName || bookingGroup?.studentSnapshot?.fullName || (booking as any)?.studentSnapshot?.fullName || undefined,
        studentPhone: studentProfile?.phoneNumber || bookingGroup?.studentSnapshot?.phoneNumber || (booking as any)?.studentSnapshot?.phoneNumber || undefined,
      });

      if (submitRes.success && submitRes.data) {
        setSubmittedSuccess(true);
        setCountdown(4);
        if (booking) {
          setBooking((prev) =>
            prev
              ? {
                  ...prev,
                  paymentStatus: 'PENDING_VERIFICATION',
                  status: 'PENDING_PAYMENT_VERIFICATION',
                  bookingStatus: 'PENDING_PAYMENT_VERIFICATION',
                }
              : null
          );
        }
        if (bookingGroup) {
          setBookingGroup((prev) =>
            prev
              ? {
                  ...prev,
                  paymentStatus: 'PENDING_VERIFICATION',
                  status: 'PENDING',
                }
              : null
          );
        }
        await loadData();
      } else {
        setErrorMessage(submitRes.error?.messageAr || 'حدث خطأ أثناء تسجيل طلب السداد');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'حدث خطأ غير متوقع أثناء إرسال الطلب');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="simplified-checkout-wrapper" dir="rtl">
        <div style={{ textAlign: 'center', padding: '3rem', background: '#F8FAFC', borderRadius: '16px' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#475569', fontWeight: 600 }}>جاري تحميل صفحة السداد وتفاصيل الحجز...</p>
        </div>
      </div>
    );
  }

  if (errorMessage && !booking && !bookingGroup) {
    return (
      <div className="simplified-checkout-wrapper" dir="rtl">
        <div style={{ textAlign: 'center', padding: '3rem', background: '#FEF2F2', borderRadius: '16px', border: '1px solid #FCA5A5' }}>
          <AlertTriangle size={48} color="#DC2626" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ color: '#991B1B', fontWeight: 800, marginBottom: '0.5rem' }}>تنبيه</h3>
          <p style={{ color: '#7F1D1D', marginBottom: '1.5rem' }}>{errorMessage}</p>
          <Link to="/student" className="btn-primary">
            العودة للوحة الطالب
          </Link>
        </div>
      </div>
    );
  }

  if (!booking && !bookingGroup) return null;

  const currentPaymentStatus = bookingGroup ? bookingGroup.paymentStatus : booking!.paymentStatus;
  const isAlreadyPaid = currentPaymentStatus === 'PAID';
  const isPendingVerification =
    currentPaymentStatus === 'PENDING_VERIFICATION' ||
    currentPaymentStatus === 'AWAITING_BRANCH_PAYMENT';
  const isRejected = currentPaymentStatus === 'REJECTED';

  const refCode = bookingGroup ? bookingGroup.groupCode : booking!.bookingCode;
  const displayTotal = bookingGroup ? bookingGroup.totalAmount : booking!.price;

  // Active target account based on selected method
  const targetRecipient =
    selectedMethod === 'INSTAPAY' ? settings.instapayRecipient : settings.walletRecipient;
  const targetNumber =
    selectedMethod === 'INSTAPAY' ? settings.instapayNumber : settings.walletNumber;

  // Check if at least one payment method is enabled by the admin
  const hasAnyMethod = Boolean(
    settings.instapayEnabled || settings.walletEnabled || settings.branchPaymentEnabled
  );

  return (
    <div className="simplified-checkout-wrapper" dir="rtl">
      {/* Top Breadcrumb */}
      <div className="simplified-breadcrumbs no-print">
        <Link to="/student">لوحة الطالب</Link>
        <span className="breadcrumb-sep">/</span>
        <Link to="/student/bookings">حجوزاتي</Link>
        <span className="breadcrumb-sep">/</span>
        <span>سداد الحجز</span>
      </div>

      {/* 1. HERO AMOUNT BANNER (يظهر المبلغ بوضوح) */}
      <div className="payment-amount-hero">
        <div className="hero-info-side">
          <span className="hero-ref-pill">كود الحجز: {refCode}</span>
          <h1 className="hero-title-text">سداد قيمة الرحلة</h1>
          <p className="hero-desc-text">
            {booking
              ? `رحلة ${booking.universityName} — المقعد رقم ${booking.seatNumber} — موعد السفر ${booking.tripDate}`
              : `مجموعة حجز ${bookingGroup?.itemCount} رحلات — الوجهة: ${bookingGroup?.bookings?.[0]?.universityName || 'الجامعة'}`}
          </p>
        </div>

        <div className="hero-amount-side">
          <span className="amount-label-tiny">المبلغ المطلوب سداده</span>
          <div className="amount-display-flex">
            <span className="num">{displayTotal}</span>
            <span className="curr">ج.م</span>
          </div>
        </div>
      </div>

      {/* Error alert if any */}
      {errorMessage && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '0.85rem 1.25rem', borderRadius: '12px', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <AlertTriangle size={18} />
          <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{errorMessage}</span>
        </div>
      )}

      {/* 1. STATE: ALREADY PAID */}
      {isAlreadyPaid ? (
        <div style={{ background: 'white', border: '2px solid #BBF7D0', borderRadius: '20px', padding: '2.5rem 1.5rem', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
          <CheckCircle2 size={56} color="#16A34A" style={{ margin: '0 auto 1rem' }} />
          <h2 style={{ color: '#14532D', fontWeight: 800, fontSize: '1.5rem', marginBottom: '0.5rem' }}>
            تم سداد وتأكيد الحجز بنجاح!
          </h2>
          <p style={{ color: '#166534', marginBottom: '1.5rem' }}>
            تذكرتك الرقمية معتمدة ومفعلة بالكامل وجاهزة للاستخدام عند الصعود للسيارة.
          </p>
          <div>
            {booking ? (
              <Link to={`/student/booking/${booking.id}`} className="btn-primary">
                عرض التذكرة الرقمية المعتمدة
              </Link>
            ) : (
              <Link to="/student/bookings" className="btn-primary">
                عرض تذاكر الحجز في حجوزاتي
              </Link>
            )}
          </div>
        </div>
      ) : /* 2. STATE: PENDING VERIFICATION */
      (submittedSuccess || isPendingVerification) ? (
        <div style={{ background: 'white', border: '2px solid #3B82F6', borderRadius: '24px', padding: '2.75rem 1.75rem', textAlign: 'center', boxShadow: '0 8px 30px rgba(37,99,235,0.1)' }}>
          <div style={{ width: '76px', height: '76px', borderRadius: '50%', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', border: '2px solid #BFDBFE' }}>
            <CheckCircle2 size={46} color="#2563EB" />
          </div>

          <div style={{ display: 'inline-block', background: '#DBEAFE', color: '#1E40AF', padding: '0.35rem 1.15rem', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 800, marginBottom: '0.85rem' }}>
            ✓ تم تسجيل وتأكيد إرسال الطلب
          </div>

          <h2 style={{ color: '#1E3A8A', fontWeight: 900, fontSize: '1.55rem', marginBottom: '0.75rem' }}>
            {currentPaymentStatus === 'AWAITING_BRANCH_PAYMENT'
              ? 'طلب الدفع في الفرع مسجل بنجاح — جاري المراجعة'
              : 'تم نجاح عملية الحجز وجاري المراجعة من قبل الإدارة'}
          </h2>

          <p style={{ color: '#334155', maxWidth: '560px', margin: '0 auto 1.5rem', fontSize: '0.98rem', lineHeight: '1.7' }}>
            {currentPaymentStatus === 'AWAITING_BRANCH_PAYMENT' ? (
              <>
                تم حجز مقعدك بنجاح. يرجى التوجه إلى مقر الشركة في{' '}
                <strong>{settings.branchName} ({settings.branchAddress})</strong> لسداد مبلغ{' '}
                <strong>{displayTotal} ج.م</strong> نقدًا خلال 24 ساعة لتأكيد الحجز.
              </>
            ) : (
              <>
                تم استلام إثبات السداد بنجاح وحفظه في النظام. طلبك الآن قيد المراجعة والتدقيق من قبل الإدارة، وسيتم تفعيل تذكرتك الرقمية فور الاعتماد وإشعارك في قسم الإشعارات.
              </>
            )}
          </p>

          {countdown !== null && (
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '0.75rem 1.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem' }}>
              <Clock size={20} color="#2563EB" className="animate-spin" />
              <span style={{ color: '#475569', fontSize: '0.95rem', fontWeight: 700 }}>
                جاري توجيهك تلقائياً إلى قسم رحلاتي خلال <strong style={{ color: '#2563EB', fontSize: '1.15rem' }}>{countdown}</strong> ثوانٍ...
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/student/bookings')}
              style={{ padding: '0.85rem 1.85rem', fontSize: '1rem', fontWeight: 800, borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
            >
              <span>الانتقال إلى قسم رحلاتي الآن ◄</span>
            </button>
            <Link
              to="/student"
              className="btn-secondary"
              style={{ padding: '0.85rem 1.5rem', fontSize: '0.95rem', fontWeight: 700, borderRadius: '12px' }}
            >
              لوحة تحكم الطالب
            </Link>
          </div>
        </div>
      ) : (
        /* 3. STATE: FORM WITH THE 3 CHOICE BUTTONS */
        <div>
          {/* If previously rejected, show clear note */}
          {isRejected && (
            <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem', color: '#9F1239' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 800, marginBottom: '0.35rem' }}>
                <AlertTriangle size={18} />
                <span>تعذر اعتماد إثبات التحويل السابق من قبل الإدارة</span>
              </div>
              {(activeAttempt?.rejectionReason || (bookingGroup as any)?.rejectionReason || (booking as any)?.rejectionReason) && (
                <p style={{ fontSize: '0.85rem', margin: '0 0 0.25rem 0' }}>
                  <strong>سبب الرفض:</strong> {activeAttempt?.rejectionReason || (bookingGroup as any)?.rejectionReason || (booking as any)?.rejectionReason}
                </p>
              )}
              <p style={{ fontSize: '0.8rem', color: '#BE123C', margin: 0 }}>
                يرجى التأكد من صحة بيانات التحويل وإرفاق إيصال السداد الصحيح أدناه ثم الضغط على زر (إعادة إرسال إثبات السداد).
              </p>
            </div>
          )}

          {/* THE OPTIONS HEADER */}
          <div className="payment-methods-selector-header">
            <h2 className="selector-heading">اختر وسيلة الدفع المناسبة:</h2>
            {hasAnyMethod && (
              <span style={{ fontSize: '0.85rem', color: '#64748B', fontWeight: 700 }}>
                ({[settings.instapayEnabled && 'إنستاباي', settings.walletEnabled && 'المحافظ الذكية', settings.branchPaymentEnabled && 'الدفع بالفرع'].filter(Boolean).length} وسيلة متاحة)
              </span>
            )}
          </div>

          {/* DYNAMIC CHOICE BUTTONS (ONLY ENABLED PAYMENT METHODS APPEAR) */}
          {hasAnyMethod ? (
            <div className="three-methods-grid">
              {/* Button 1: instaPay */}
              {settings.instapayEnabled && (
                <button
                  type="button"
                  id="checkout-method-instapay"
                  className={`method-choice-btn ${selectedMethod === 'INSTAPAY' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedMethod('INSTAPAY');
                    setErrorMessage(null);
                  }}
                >
                  <div className="method-icon-bubble instapay">
                    <Smartphone size={22} />
                  </div>
                  <h3 className="method-btn-title">instaPay</h3>
                  <span className="method-btn-subtitle">تحويل بنكي فوري مباشر</span>
                  {selectedMethod === 'INSTAPAY' && <span className="active-check-badge">✓</span>}
                </button>
              )}

              {/* Button 2: محافظ بنكية */}
              {settings.walletEnabled && (
                <button
                  type="button"
                  id="checkout-method-wallet"
                  className={`method-choice-btn ${selectedMethod === 'WALLET' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedMethod('WALLET');
                    setErrorMessage(null);
                  }}
                >
                  <div className="method-icon-bubble wallet">
                    <CreditCard size={22} />
                  </div>
                  <h3 className="method-btn-title">محافظ بنكية</h3>
                  <span className="method-btn-subtitle">فودافون كاش / المحافظ الذكية</span>
                  {selectedMethod === 'WALLET' && <span className="active-check-badge">✓</span>}
                </button>
              )}

              {/* Button 3: الدفع في الفرع خلال 24 ساعة */}
              {settings.branchPaymentEnabled && (
                <button
                  type="button"
                  id="checkout-method-branch"
                  className={`method-choice-btn ${selectedMethod === 'BRANCH' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedMethod('BRANCH');
                    setErrorMessage(null);
                  }}
                >
                  <div className="method-icon-bubble branch">
                    <Building2 size={22} />
                  </div>
                  <h3 className="method-btn-title">الدفع في الفرع خلال 24 ساعة</h3>
                  <span className="method-btn-subtitle">سداد نقدي في مقر الشركة</span>
                  {selectedMethod === 'BRANCH' && <span className="active-check-badge">✓</span>}
                </button>
              )}
            </div>
          ) : (
            <div style={{ background: '#FFFBEB', border: '1.5px solid #FCD34D', borderRadius: '16px', padding: '2rem 1.5rem', textAlign: 'center', marginBottom: '1.75rem' }}>
              <AlertTriangle size={42} color="#D97706" style={{ margin: '0 auto 0.75rem' }} />
              <h3 style={{ color: '#B45309', fontWeight: 800, fontSize: '1.25rem', marginBottom: '0.5rem' }}>
                طرق الدفع معطلة مؤقتًا
              </h3>
              <p style={{ color: '#92400E', fontSize: '0.95rem', maxWidth: '500px', margin: '0 auto 1.25rem', lineHeight: '1.6' }}>
                نعتذر منك، لقد تم إيقاف استقبال المدفوعات مؤقتًا للتحديث من قبل إدارة النظام. يرجى المحاولة لاحقاً أو التواصل مع خدمة العملاء.
              </p>
              <Link to="/student" className="btn-secondary" style={{ padding: '0.75rem 1.5rem', fontSize: '0.95rem', borderRadius: '12px', display: 'inline-block' }}>
                العودة للوحة تحكم الطالب
              </Link>
            </div>
          )}

          {/* ACTIVE METHOD DETAILS CARD */}
          {hasAnyMethod && (
            <div className="active-method-card">
              {selectedMethod === 'BRANCH' ? (
              /* BRANCH VIEW (الدفع في الفرع خلال 24 ساعة) */
              <div>
                <div className="branch-card-content">
                  <div className="branch-header-row">
                    <Building2 size={22} />
                    <span>سداد نقدي بمقر فرع OTO خلال 24 ساعة</span>
                  </div>

                  <div className="branch-details-grid">
                    <div>
                      <span style={{ color: '#92400E', fontSize: '0.785rem', display: 'block' }}>مقر الفرع:</span>
                      <strong>{settings.branchName}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#92400E', fontSize: '0.785rem', display: 'block' }}>العنوان:</span>
                      <span>{settings.branchAddress}</span>
                    </div>
                    <div>
                      <span style={{ color: '#92400E', fontSize: '0.785rem', display: 'block' }}>مواعيد العمل:</span>
                      <span>{settings.branchWorkingHours}</span>
                    </div>
                    <div>
                      <span style={{ color: '#92400E', fontSize: '0.785rem', display: 'block' }}>هاتف الفرع:</span>
                      <span dir="ltr">{settings.branchPhone}</span>
                    </div>
                  </div>

                  {settings.branchInstructions && (
                    <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', padding: '0.75rem 1rem', borderRadius: '10px', fontSize: '0.85rem', color: '#92400E', margin: '0.85rem 0' }}>
                      <strong>تعليمات سداد الفرع للطلاب:</strong> {settings.branchInstructions}
                    </div>
                  )}

                  <div className="branch-deadline-alert">
                    <Clock size={16} />
                    <span>
                      مهلة السداد: يرجى التوجه للفرع وسداد مبلغ <strong>{displayTotal} ج.م</strong> خلال 24 ساعة كحد أقصى لتأكيد حجز المقعد.
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-submit-payment-main"
                  disabled={isSubmitting}
                  onClick={handleSubmitPayment}
                >
                  {isSubmitting ? 'جاري التأكيد...' : 'تأكيد الحجز والدفع في الفرع خلال 24 ساعة'}
                </button>
              </div>
            ) : (
              /* INSTAPAY & WALLET TRANSFER VIEW */
              <form onSubmit={handleSubmitPayment}>
                {/* 1. Account Target Display with Copy Button */}
                <div className="transfer-account-card">
                  <div className="transfer-target-row">
                    <div className="target-info-group">
                      <span className="target-label-text">
                        {selectedMethod === 'INSTAPAY'
                          ? 'حساب / رقم إنستاباي المعتمد للتحويل:'
                          : 'رقم المحفظة المعتمد للتحويل:'}
                      </span>
                      <code className="target-code-display">{targetNumber}</code>
                      <span className="target-recipient-badge">
                        المستفيد: <strong>{targetRecipient}</strong>
                      </span>
                    </div>

                    <button
                      type="button"
                      className="btn-copy-target"
                      onClick={() => handleCopy(targetNumber)}
                    >
                      {copiedAccount ? (
                        <>
                          <Check size={16} />
                          <span>تم النسخ بنجاح ✓</span>
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          <span>نسخ الرقم</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Wallet Provider Selector (Only when 'محافظ بنكية' is selected) */}
                {selectedMethod === 'WALLET' && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label className="form-label">اختر شبكة المحفظة المحول منها/إليها:</label>
                    <div className="wallet-providers-list">
                      {(settings.walletProviders || ['VODAFONE_CASH', 'ORANGE_CASH', 'ETISALAT_CASH', 'WE_PAY']).map((prov) => (
                        <button
                          key={prov}
                          type="button"
                          className={`wallet-provider-chip ${selectedWalletProvider === prov ? 'active' : ''}`}
                          onClick={() => setSelectedWalletProvider(prov)}
                        >
                          {prov === 'VODAFONE_CASH'
                            ? 'فودافون كاش'
                            : prov === 'ORANGE_CASH'
                            ? 'أورنج كاش'
                            : prov === 'ETISALAT_CASH'
                            ? 'اتصالات كاش'
                            : prov === 'WE_PAY'
                            ? 'وي باي (WE)'
                            : 'المحافظ الذكية البنكية'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2. Sender Account / Phone Number */}
                <div className="form-field-group">
                  <label className="form-label" htmlFor="transferFromInput">
                    {selectedMethod === 'INSTAPAY'
                      ? 'رقم الهاتف أو عنوان الحساب الذي قمت بالتحويل منه:'
                      : 'رقم المحفظة التي قمت بالتحويل منها:'}
                  </label>
                  <input
                    id="transferFromInput"
                    type="text"
                    className="text-input-field"
                    placeholder={
                      selectedMethod === 'INSTAPAY'
                        ? 'مثال: 01012345678 أو username@instapay'
                        : 'مثال: 01012345678'
                    }
                    value={transferFrom}
                    onChange={(e) => setTransferFrom(e.target.value)}
                    required
                  />
                </div>

                {/* 3. Upload Screenshot Zone */}
                <div className="form-field-group">
                  <label className="form-label">صورة إشعار التحويل (Screenshot):</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />

                  {isCompressing ? (
                    <div className="proof-upload-zone" style={{ padding: '2rem' }}>
                      <div className="upload-inner-content">
                        <Clock size={32} color="#0284C7" className="animate-spin" />
                        <span className="upload-title">جاري معالجة وتحسين صورة الإثبات فوراً...</span>
                        <span className="upload-hint">يتم ضغط الصورة لضمان وضوح الأرقام وحفظها بنجاح في قاعدة البيانات</span>
                      </div>
                    </div>
                  ) : proofPreviewUrl ? (
                    <div className="proof-preview-container">
                      <img
                        src={proofPreviewUrl}
                        alt="إشعار التحويل"
                        className="proof-preview-img"
                      />
                      <button
                        type="button"
                        className="btn-remove-preview"
                        onClick={handleRemoveProof}
                        title="حذف الصورة واختيار صورة أخرى"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      className="proof-upload-zone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="upload-inner-content">
                        <Upload size={32} color="#0284C7" />
                        <span className="upload-title">اضغط هنا لرفع صورة إشعار أو رسالة التحويل</span>
                        <span className="upload-hint">صيغ مدعومة: JPG, PNG, WEBP (يتم تحسينها وحفظها تلقائياً)</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* 4. Main CTA Button */}
                <button
                  type="submit"
                  className="btn-submit-payment-main"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>جاري رفع الإثبات وتسجيل الطلب...</span>
                  ) : (
                    <>
                      <span>{isRejected ? 'إعادة إرسال إثبات السداد للمراجعة ↺' : 'تأكيد وإرسال إثبات السداد للإدارة'}</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Collapsible Trip Details */}
            <button
              type="button"
              className="trip-summary-toggle-btn"
              onClick={() => setShowTripDetails(!showTripDetails)}
            >
              {showTripDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <span>{showTripDetails ? 'إخفاء تفاصيل الرحلة والمقاعد' : 'عرض تفاصيل الرحلة والمقاعد المحجوزة'}</span>
            </button>

            {showTripDetails && (
              <div className="collapsible-trip-details">
                {booking && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.65rem', fontSize: '0.85rem', color: '#475569' }}>
                    <div>
                      <span style={{ display: 'block', color: '#64748B', fontSize: '0.785rem' }}>الجامعة:</span>
                      <strong>{booking.universityName}</strong>
                    </div>
                    <div>
                      <span style={{ display: 'block', color: '#64748B', fontSize: '0.785rem' }}>تاريخ السفر:</span>
                      <strong>{booking.tripDate}</strong>
                    </div>
                    <div>
                      <span style={{ display: 'block', color: '#64748B', fontSize: '0.785rem' }}>موعد التحرك:</span>
                      <strong>{booking.departureTime}</strong>
                    </div>
                    <div>
                      <span style={{ display: 'block', color: '#64748B', fontSize: '0.785rem' }}>المقعد:</span>
                      <strong style={{ color: '#0284C7' }}>المقعد رقم {booking.seatNumber}</strong>
                    </div>
                  </div>
                )}

                {bookingGroup && (
                  <div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0F172A', display: 'block', marginBottom: '0.5rem' }}>
                      الرحلات المتضمنة في المجموعة ({bookingGroup.itemCount} رحلات):
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {(bookingGroup.bookings || []).map((b, idx) => (
                        <div
                          key={idx}
                          style={{ background: 'white', border: '1px solid #E2E8F0', padding: '0.5rem 0.75rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}
                        >
                          <span>
                            <strong>رحلة {idx + 1}:</strong> {b.date} ({b.time}) — مقعد <strong>{b.seatNumber}</strong>
                          </span>
                          <span style={{ fontWeight: 700, color: '#0F172A' }}>{b.price} ج.م</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};
